/**
 * Seat — ONE platform-wide switch: may people connect their own Claude plan?
 *
 * Today the personal seat exists for Claude only (Codex seat-connect was
 * never built — the OpenAI row here would be a dead switch, so it isn't
 * shown). Backed by InferenceProvider.allowPersonalSeat on the 'anthropic'
 * vendor row, enforced in THREE places so the flip really governs:
 *   • connect UI  — /api/seats stops offering Connect (SeatSettings),
 *   • expert workbench connect UI,
 *   • expert developer-lane credential resolution.
 * Open Workspace and Project Workspace always use the managed runtime and do
 * not consult this switch or an already-connected seat.
 */

import * as React from "react";
import { UserRound } from "lucide-react";
import { Switch } from "./ui";
import { toast } from "react-toastify";
import { adminFetch } from "./adminFetch";

interface ProviderRow { key: string; kind: string; label: string; allowPersonalSeat: boolean }

export function SeatPanel() {
  const [row, setRow] = React.useState<ProviderRow | null | undefined>(undefined); // undefined = loading
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const res = await adminFetch("/api/admin/providers");
      const d = await res.json().catch(() => ({}));
      const anthropic = (Array.isArray(d?.providers) ? d.providers : []).find((p: ProviderRow) => p.key === "anthropic" && p.kind === "vendor") || null;
      setRow(anthropic);
    } catch {
      toast.error("Failed to load seat setting");
      setRow(null);
    }
  }, []);
  React.useEffect(() => { void load(); }, [load]);

  const toggle = async () => {
    if (!row) return;
    setBusy(true);
    try {
      const res = await adminFetch("/api/admin/providers", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: "anthropic", patch: { allowPersonalSeat: !row.allowPersonalSeat } }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || "save failed");
      toast.success(!row.allowPersonalSeat ? "Personal seats are ON — people can connect their own Claude plan." : "Personal seats are OFF — everyone runs on the swarm.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  if (row === undefined) return <p className="text-xs text-[var(--dash-text-muted)]">Loading…</p>;

  if (row === null) {
    return (
      <p className="text-[13px] text-[var(--dash-text-secondary)]">
        No Anthropic key is configured yet — add it on the <b>Keys</b> tab first. The seat switch lives on that credential.
      </p>
    );
  }

  const on = row.allowPersonalSeat;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-4 rounded-xl border border-[var(--dash-border)] bg-[var(--dash-panel-bg)] p-4">
        <UserRound className="h-5 w-5 shrink-0 text-[var(--dash-text-muted)]" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-[var(--dash-text)]">Personal Claude seats</p>
          <p className="mt-0.5 text-[12.5px] leading-relaxed text-[var(--dash-text-secondary)]">
            {on
              ? "Experts can use their own Claude subscription in their private developer workbench."
              : "Expert workbenches use AA Compass instead of a personal Claude subscription."}
          </p>
        </div>
        <Switch checked={on} disabled={busy} onCheckedChange={() => void toggle()} aria-label="Allow personal Claude seats platform-wide" />
      </div>
      <p className="px-1 text-[11.5px] leading-relaxed text-[var(--dash-text-muted)]">
        Open Workspace, Planner, builds and QA always use the managed runtime. This switch applies only to expert developer workbenches.
        A Codex seat doesn&apos;t exist yet; when it ships it gets its own switch here.
      </p>
    </div>
  );
}
